import { CustomerAuth, FormSection } from '@alinta-digital/asg';
import { ICustomerAuthFields } from '@alinta-digital/asg/components/CustomerAuth/ICustomerAuthFields';
import { useLazyQuery } from '@apollo/client/';
import { FormikProps } from 'formik';
import React from 'react';
import { Element } from 'react-scroll';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import { FuelSite } from '../../types';
import { VALIDATE_CUSTOMER } from '../../utils/Account.gql';
import {
  scrollToScrollElement,
  setFormContextCustomer,
  setFormContextError,
} from '../../utils/utils';
import { DirectDebitForm } from '../../views/Form/Form';
import { ErrorNotification } from '../ErrorNotification';
import { ButtonV2 } from '../../Common.Styled';
import { pushAnalytics } from '../../utils/utils';
import { analyticsLoaderStep1 } from '../../utils/analytics.helper';

export interface IdentificationParentForm {
  identifyYourAccountForm: ICustomerAuthFields;
}

interface IdentificationSubFormProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onNextClick: () => void;
}

let submitCustAuth: () => void;

export const IdentificationSubFormCore = ({
  formikProps,
  onNextClick,
}: IdentificationSubFormProps) => {
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [disableSubmit, setDisableSubmit] = React.useState(true);

  const [validateCustomerAccount] = useLazyQuery(VALIDATE_CUSTOMER, {
    fetchPolicy: 'network-only',
    onCompleted: /* istanbul ignore next */ (response) => {
      const data = response.validateCustomerWebsite[0];
      const fuelTypes: FuelSite[] = [
        {
          authAccountNumber: data.account.accountId,
          accountNumber: data.account.accountId,
          fuelType: data.account.fuel,
          accountType: data.account?.accountType?.toLowerCase(),
        },
      ];
      setFormContextCustomer(
        formContextValues,
        formContextDispatch,
        data.primaryContactCustomerNumber,
        '',
        fuelTypes
      );
      setSubmitLoading(false);

      onNextClick();
    },
    onError: /* istanbul ignore next */ () => {
      setSubmitLoading(false);
      setFormContextError(
        formContextValues,
        formContextDispatch,
        0,
        true,
        undefined
      );
    },
  });

  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);
  const formContextError = formContextValues.error;

  React.useEffect(() => {
    if (formContextError.steps[0].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('identificationStart');
    }
  }, [formContextError.steps]);

  React.useEffect(() => {
    analyticsLoaderStep1();
  }, []);
  return (
    <FormSection
      id="Identification"
      heading="Step 1: Your account"
      stepNumber="1"
      totalSteps="4"
      nextButton={
        !formContextError.steps[0].status ? (
          <ButtonV2
            id="identificationNextButton"
            data-testid="identification_nextButton"
            loading={submitLoading}
            disabled={disableSubmit}
            type="submit"
            primary={true}
            onClick={async (event: any) => {
              event.preventDefault();
              pushAnalytics('click.5.dd.s1.next');
              submitCustAuth();
            }}
          >
            Next
          </ButtonV2>
        ) : (
          undefined
        )
      }
    >
      {formContextError.steps[0].status ? (
        <ErrorNotification
          errorType={formContextError.steps[0].type}
          onClick={() => {
            setFormContextError(
              formContextValues,
              formContextDispatch,
              0,
              false,
              undefined
            );
          }}
          stateShortName={formContextValues.stateShortName}
        />
      ) : (
        <>
          <Element name="identificationStart" id="identificationStart" />
          <CustomerAuth<DirectDebitForm, IdentificationParentForm>
            accountType="accountCore"
            formikProps={formikProps}
            subFormConfig={(values, validationConfig) => ({
              identifyYourAccountForm: {
                accountNumber: {
                  initialValue: values.identifyYourAccountForm.accountNumber,
                  validation:
                    validationConfig.identifyYourAccountForm.accountNumber,
                },
                firstName: {
                  initialValue: values.identifyYourAccountForm.firstName,
                  validation:
                    validationConfig.identifyYourAccountForm.firstName,
                },
                lastName: {
                  initialValue: values.identifyYourAccountForm.lastName,
                  validation: validationConfig.identifyYourAccountForm.lastName,
                },
                dob: {
                  initialValue: values.identifyYourAccountForm.dob,
                  validation: validationConfig.identifyYourAccountForm.dob,
                },
              },
            })}
            accountTooltipMsg="Please enter the first 9 digits of your electricity or gas account / reference number found on your welcome pack or bill"
            valuesKey={'identifyYourAccountForm'}
          >
            {({ subFormFormikProps, validateSubForm }) => {
              const fieldHasErrors = Object.keys(subFormFormikProps.errors)
                .length
                ? true
                : false;
              let allFieldsPopulated = true;
              const values: { [key: string]: string } =
                subFormFormikProps.values.identifyYourAccountForm;
              const keys: string[] = Object.keys(values);
              for (const key of keys) {
                allFieldsPopulated = !values[key].length ? false : true;
              }
              if (!disableSubmit && fieldHasErrors) {
                setDisableSubmit(true);
              } else if (
                allFieldsPopulated &&
                disableSubmit &&
                !fieldHasErrors
              ) {
                setDisableSubmit(false);
              }

              submitCustAuth = async () => {
                const { identifyYourAccountForm } = subFormFormikProps.values;

                // saves value to parentForm if onEnter when user did not onBlur auth fields
                await formikProps.setValues({
                  ...formikProps.values,
                  identifyYourAccountForm: { ...identifyYourAccountForm },
                });

                setSubmitLoading(true);
                const isCustAuthFormValid = (await validateSubForm()).isValid;

                if (!isCustAuthFormValid) {
                  setSubmitLoading(false);
                  return false;
                }

                const dob = identifyYourAccountForm.dob.split('/');

                formikProps.setValues({
                  ...formikProps.values,
                  ...subFormFormikProps.values,
                });

                try {
                  await validateCustomerAccount({
                    variables: {
                      accountNumber: identifyYourAccountForm.accountNumber,
                      dob: `${dob[2]}-${dob[1]}-${dob[0]}`,
                      firstName: identifyYourAccountForm.firstName,
                      lastName: identifyYourAccountForm.lastName,
                    },
                  });
                } catch (error) {}
              };
              return true;
            }}
          </CustomerAuth>
        </>
      )}
    </FormSection>
  );
};
